/**
 * Proposals — what the builder suggests changing in the agent, grouped in "chapters". The adviser
 * approves a whole chapter or single items, edits an item before approving, dismisses, and later undoes
 * an applied change. Nothing reaches the agent without one of these clicks (or a clear "yes" in the
 * conversation).
 *
 * API: GET /agent-builder/proposals?status=proposed|applied · PUT /agent-builder/proposals/:id ·
 *      POST /agent-builder/proposals/apply|reject {ids|groupId} · POST /agent-builder/proposals/:id/undo
 */
import React, { useState } from 'react';
import { useToast } from '../../Context/ToastContext';
import {
  Box, Row, Pill, Muted, Help, SmallBtn, OkBtn, DangerBtn, PrimaryBtn, GhostBtn, TextArea, Input, Pre, Empty,
  KIND_LABEL, OP_LABEL, STATUS_LABEL, errText, fmtWhen,
} from './styles';

const SOURCE_LABEL = { reference: 'From iMaven reference agent', document: 'From your document', test: 'From a test', conversation: 'From our conversation', web: 'From official sources (research)', learning: 'From your recent conversations' };

function ProposalCard({ p, api, reload, readOnly, onApplied }) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [summary, setSummary] = useState(p.summary);
  const [busy, setBusy] = useState(false);
  const [op, opBg, opFg] = OP_LABEL[p.op] || [p.op];
  const [st, stBg, stFg] = STATUS_LABEL[p.status] || [p.status];

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.agentNow) onApplied?.(r.data.agentNow);
      const failed = (r?.data?.results || []).find(x => x.status !== 'applied');
      if (failed) showToast(failed.error || 'It could not be applied — see the card.', 'warning'); else if (ok) showToast(ok, 'success');
      await reload();
    } catch (e) { showToast(errText(e, 'That did not work'), 'error'); } finally { setBusy(false); }
  };
  const startEdit = () => { setDraft(JSON.stringify(p.payload || {}, null, 2)); setSummary(p.summary); setEditing(true); setOpen(true); };
  const saveEdit = () => {
    let payload;
    try { payload = JSON.parse(draft); } catch { showToast('The item is not valid JSON.', 'error'); return; }
    act(() => api.put(`/agent-builder/proposals/${p.id}`, { payload, summary }), 'Saved — approve it when ready').then(() => setEditing(false));
  };

  return (
    <Box>
      <Row style={{ justifyContent: 'space-between' }}>
        <Row>
          <Pill $bg={opBg} $fg={opFg}>{op}</Pill>
          <Pill>{KIND_LABEL[p.kind] || p.kind}</Pill>
          <Muted>{p.targetKey}</Muted>
        </Row>
        <Pill $bg={stBg} $fg={stFg}>{st}</Pill>
      </Row>
      <div style={{ fontSize: 13, lineHeight: 1.5, margin: '8px 0 4px' }}>{p.summary}</div>
      {p.reason && <Help>{p.reason}</Help>}
      {p.warnings?.length > 0 && p.warnings.map((w, i) => <Help key={i} style={{ color: '#e3b341' }}>⚠ {w}</Help>)}
      {p.error && <Help style={{ color: '#ff7b72' }}>{p.error}</Help>}
      {p.sources?.length > 0 && (
        <Help>Sources: {p.sources.map((s, i) => (s.url ? <a key={i} href={s.url} target="_blank" rel="noreferrer" style={{ color: '#79c0ff', marginRight: 8 }}>{s.note || s.url}</a> : <span key={i} style={{ marginRight: 8 }}>{s.note}</span>))}</Help>
      )}
      {p.appliedAt && <Help>Applied {fmtWhen(p.appliedAt)}{p.appliedVia ? ` · ${p.appliedVia}` : ''}</Help>}

      {open && !editing && p.payload && <Pre>{JSON.stringify(p.payload, null, 2)}</Pre>}
      {editing && (
        <div style={{ marginTop: 8 }}>
          <Muted>What it changes (shown on the card)</Muted>
          <Input value={summary} onChange={e => setSummary(e.target.value)} style={{ margin: '4px 0 8px' }} />
          <Muted>The item itself (English; the builder checks it again when you save)</Muted>
          <TextArea $mono rows={14} value={draft} onChange={e => setDraft(e.target.value)} style={{ marginTop: 4 }} />
          <Row style={{ marginTop: 8 }}>
            <PrimaryBtn disabled={busy} onClick={saveEdit}>Save</PrimaryBtn>
            <GhostBtn disabled={busy} onClick={() => setEditing(false)}>Cancel</GhostBtn>
          </Row>
        </div>
      )}

      <Row style={{ marginTop: 10 }}>
        {p.payload && !editing && <SmallBtn onClick={() => setOpen(o => !o)}>{open ? 'Hide details' : 'Details'}</SmallBtn>}
        {!readOnly && ['proposed', 'conflict', 'failed'].includes(p.status) && (
          <>
            {p.op !== 'delete' && !editing && <SmallBtn disabled={busy} onClick={startEdit}>Edit</SmallBtn>}
            <OkBtn disabled={busy} onClick={() => act(() => api.post('/agent-builder/proposals/apply', { ids: [p.id] }), p.op === 'delete' ? 'Removed from your agent' : 'Added to your agent')}>{p.op === 'delete' ? 'Approve removal' : 'Approve'}</OkBtn>
            <DangerBtn disabled={busy} onClick={() => act(() => api.post('/agent-builder/proposals/reject', { ids: [p.id] }))}>Dismiss</DangerBtn>
          </>
        )}
        {p.status === 'applied' && (
          <SmallBtn disabled={busy} onClick={() => act(() => api.post(`/agent-builder/proposals/${p.id}/undo`), 'Undone')}>Undo</SmallBtn>
        )}
      </Row>
    </Box>
  );
}

export default function ProposalsPanel({ api, groups, applied, view, reload, focusGroupId, onApplied }) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState('');
  const list = view === 'applied' ? applied : groups;

  const groupAct = async (g, path, ok) => {
    setBusy(g.groupId);
    try {
      const r = await api.post(`/agent-builder/proposals/${path}`, { groupId: g.groupId });
      if (r.data?.agentNow) onApplied?.(r.data.agentNow);
      const failed = (r.data?.results || []).filter(x => x.status !== 'applied');
      if (path === 'apply' && failed.length) showToast(`${r.data.applied} applied, ${failed.length} need a look (see the cards).`, 'warning');
      else showToast(ok, 'success');
      await reload();
    } catch (e) { showToast(errText(e, 'That did not work'), 'error'); } finally { setBusy(''); }
  };

  if (!list.length) {
    return <Empty>{view === 'applied' ? 'Nothing applied yet. Changes you approve appear here, each with Undo.' : 'Nothing waiting for you. Ask the builder what to improve next, or drop a document into the chat.'}</Empty>;
  }
  return (
    <div>
      {list.map(g => {
        const waiting = g.proposals.filter(p => ['proposed', 'conflict', 'failed'].includes(p.status));
        return (
          <div key={g.groupId} id={`group-${g.groupId}`} style={{ marginBottom: 16, outline: focusGroupId === g.groupId ? '1px solid #2f81f7' : 'none', borderRadius: 12, padding: focusGroupId === g.groupId ? 6 : 0 }}>
            <Row style={{ justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{g.title}</div>
                <Muted>{SOURCE_LABEL[g.source] || g.source} · {fmtWhen(g.createdAt)}</Muted>
              </div>
              {view !== 'applied' && waiting.length > 0 && (
                <Row>
                  <PrimaryBtn disabled={!!busy} onClick={() => groupAct(g, 'apply', `${waiting.length} change${waiting.length === 1 ? '' : 's'} added to your agent`)}>Approve all ({waiting.length})</PrimaryBtn>
                  <GhostBtn disabled={!!busy} onClick={() => groupAct(g, 'reject', 'Dismissed')}>Dismiss all</GhostBtn>
                </Row>
              )}
            </Row>
            {g.proposals.map(p => <ProposalCard key={p.id} p={p} api={api} reload={reload} onApplied={onApplied} readOnly={view === 'applied' && p.status !== 'applied'} />)}
          </div>
        );
      })}
    </div>
  );
}
